import React, { useState } from 'react';
import {
  Building2,
  Package,
  Truck,
  Globe,
  Boxes,
  Factory,
  GitBranch,
  Upload,
} from 'lucide-react';
import { Badge } from '../UI/badge';
import { Button } from '../UI/button';
import { ModalDialog } from '../UI/modal-dialog';
import { useLocale } from '../../contexts/LocaleContext';
import {
  TENANT_NODE_TABS,
  TENANT_DATA_IMPORT_SECTION,
  isDataImportSection,
  getNodeTabByPageTab,
} from '../../config/tenantNodeTabsConfig';
import { themeClasses } from '../../config/themeClasses';
import { cn } from '../../lib/utils';
import TenantNodeTabPanel from './TenantNodeTabPanel';
import TenantCsvUploadPanel from './TenantCsvUploadPanel';

const NODE_TAB_ICONS = {
  facilities: Building2,
  products: Package,
  logistics: Truck,
  geographies: Globe,
  commodities: Boxes,
  supplierFacilities: Factory,
  graphRelationships: GitBranch,
};

function TenantSupplyChainDataLayout({
  activeDataSection,
  onDataSectionChange,
  nodesByKey,
  loadingByKey,
  savingByKey,
  uploadingByKey,
  resultsByKey,
  existingImportByKey,
  onSaveNodeRows,
  onUploadFromPanel,
}) {
  const { t } = useLocale();
  const [isTableDirty, setIsTableDirty] = useState(false);
  const [pendingSection, setPendingSection] = useState(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);

  const applySection = (section) => {
    setIsTableDirty(false);
    onDataSectionChange(section);
  };

  const requestSection = (section) => {
    if (isTableDirty && section !== activeDataSection) {
      setPendingSection(section);
      setUnsavedOpen(true);
      return;
    }
    applySection(section);
  };

  const handleDiscardAndLeave = () => {
    setUnsavedOpen(false);
    if (pendingSection != null) {
      applySection(pendingSection);
      setPendingSection(null);
    }
  };

  const activeNodeTab = isDataImportSection(activeDataSection)
    ? null
    : getNodeTabByPageTab(activeDataSection);

  const sidebarItems = [
    ...TENANT_NODE_TABS.map((tab) => ({
      id: tab.pageTab,
      label: t(tab.labelKey),
      icon: NODE_TAB_ICONS[tab.key],
      count: Array.isArray(nodesByKey[tab.key]) ? nodesByKey[tab.key].length : null,
    })),
    {
      id: TENANT_DATA_IMPORT_SECTION,
      label: t('tenantDataSidebarImport'),
      icon: Upload,
      count: null,
    },
  ];

  const renderSidebarButton = (item) => {
    const Icon = item.icon;
    const isActive = activeDataSection === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => requestSection(item.id)}
        className={cn(
          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary-50 text-primary-600'
            : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.count != null ? (
          <Badge variant="secondary" className="shrink-0 tabular-nums">
            {item.count}
          </Badge>
        ) : null}
      </button>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <nav
          className={cn(
            'hidden w-full shrink-0 flex-col gap-1 rounded-xl border border-border-light bg-background-secondary p-2 lg:flex lg:w-56'
          )}
          aria-label={t('tenantTabSupplyChainData')}
        >
          {sidebarItems.map(renderSidebarButton)}
        </nav>

        <label className="flex flex-col gap-1 lg:hidden">
          <span className="text-sm font-medium text-text-secondary">{t('tenantDataSectionSelect')}</span>
          <select
            className="rounded-lg border border-border-light bg-background-card px-3 py-2 text-sm text-text-primary"
            value={activeDataSection}
            onChange={(e) => requestSection(e.target.value)}
          >
            {sidebarItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
                {item.count != null ? ` (${item.count})` : ''}
              </option>
            ))}
          </select>
        </label>

        <main className="min-w-0 flex-1">
          {isDataImportSection(activeDataSection) ? (
            <TenantCsvUploadPanel
              uploadingByKey={uploadingByKey}
              resultsByKey={resultsByKey}
              existingByKey={existingImportByKey}
              onUpload={onUploadFromPanel}
              onOpenDataSection={onDataSectionChange}
            />
          ) : activeNodeTab ? (
            <TenantNodeTabPanel
              nodeKey={activeNodeTab.key}
              rows={nodesByKey[activeNodeTab.key] || []}
              loading={Boolean(loadingByKey[activeNodeTab.key])}
              saving={Boolean(savingByKey[activeNodeTab.key])}
              onSaveNodeRows={onSaveNodeRows}
              onDirtyChange={setIsTableDirty}
            />
          ) : null}
        </main>
      </div>

      <ModalDialog
        open={unsavedOpen}
        onClose={() => {
          setUnsavedOpen(false);
          setPendingSection(null);
        }}
        title={t('tenantUnsavedChangesTitle')}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setUnsavedOpen(false)}>
              {t('tenantUnsavedChangesStay')}
            </Button>
            <Button type="button" variant="destructive" onClick={handleDiscardAndLeave}>
              {t('tenantUnsavedChangesLeave')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('tenantUnsavedChangesDescription')}</p>
      </ModalDialog>
    </>
  );
}

export default TenantSupplyChainDataLayout;
